import { useSettingsStore } from '../store/settingsStore';
import { formatDateString, parseDateString, utcStringToDate, dateToUTCString, extractDateOnlyString } from './dateConverter';

// Number formatting utilities
export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '0';
  }

  const settings = useSettingsStore.getState();
  const { decimalPlaces, thousandsSeparator, decimalSeparator } = settings.numberFormat;

  // Format the number
  const fixed = num.toFixed(decimalPlaces);
  const parts = fixed.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';

  // Add thousands separator
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  // Combine parts
  if (decimalPlaces > 0 && decimalPart) {
    return `${formattedInteger}${decimalSeparator}${decimalPart}`;
  }

  return formattedInteger;
}

export function parseNumber(value: string): number {
  if (!value) return 0;

  const settings = useSettingsStore.getState();
  const { thousandsSeparator, decimalSeparator } = settings.numberFormat;

  // Remove thousands separator and replace decimal separator with dot
  let cleaned = value.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '');
  cleaned = cleaned.replace(new RegExp(`\\${decimalSeparator}`, 'g'), '.');

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/** Normalize GET /api/invoices/item-stock JSON (nested wrappers, camel/snake case). */
export function parseAvailableQuantityFromStockApiPayload(data: unknown): number | undefined {
  const pickQty = (obj: Record<string, unknown>): number | undefined => {
    const raw =
      obj['availableQuantity'] ??
      obj['AvailableQuantity'] ??
      obj['available_quantity'] ??
      obj['Available_Quantity'];
    if (raw === null || raw === undefined) return undefined;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : undefined;
  };

  let cur: unknown = data;
  for (let depth = 0; depth < 5 && cur != null; depth++) {
    if (typeof cur !== 'object' || Array.isArray(cur)) return undefined;
    const o = cur as Record<string, unknown>;
    const q = pickQty(o);
    if (q !== undefined) return q;
    const next = o['data'] ?? o['result'] ?? o['payload'] ?? o['value'];
    if (next != null && typeof next === 'object' && !Array.isArray(next)) {
      cur = next;
      continue;
    }
    return undefined;
  }
  return undefined;
}

export function parseAverageCostFromStockApiPayload(data: unknown): number | undefined {
  const pickCost = (obj: Record<string, unknown>): number | undefined => {
    const raw =
      obj['averageCost'] ??
      obj['AverageCost'] ??
      obj['average_cost'] ??
      obj['Average_Cost'];
    if (raw === null || raw === undefined) return undefined;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };

  let cur: unknown = data;
  for (let depth = 0; depth < 5 && cur != null; depth++) {
    if (typeof cur !== 'object' || Array.isArray(cur)) return undefined;
    const o = cur as Record<string, unknown>;
    const cost = pickCost(o);
    if (cost !== undefined) return cost;
    const next = o['data'] ?? o['result'] ?? o['payload'] ?? o['value'];
    if (next != null && typeof next === 'object' && !Array.isArray(next)) {
      cur = next;
      continue;
    }
    return undefined;
  }
  return undefined;
}

/**
 * Get currency display symbol with special handling for Saudi Riyal
 * @param currencySymbol - The currency symbol from database
 * @param currencyName - The currency name (optional, for SAR detection)
 * @param currencyId - The currency ID (optional, for SAR detection)
 * @returns The display symbol to use
 */
export function getCurrencyDisplaySymbol(
  currencySymbol: string = '',
  currencyName?: string,
  currencyId?: string
): string {
  // If no symbol provided, we can't do much without a hook (which we can't use here)
  // But we can check common SAR identifiers
  const isSAR = 
    currencySymbol?.toUpperCase() === 'SAR' ||
    currencySymbol === 'ر.س' ||
    currencyName?.toLowerCase().includes('سعودي') ||
    currencyName?.toLowerCase().includes('riyal') ||
    currencyId === '1';
  
  if (isSAR) {
    // Use the official Saudi Riyal Unicode symbol: ﷼
    return '﷼';
  }
  
  // Return the provided symbol, or a fallback if empty
  return currencySymbol || '';
}

export function formatCurrency(value: number | string | null | undefined, currencySymbol: string = '', currencyName?: string, currencyId?: string): string {
  const formatted = formatNumber(value);
  const displaySymbol = getCurrencyDisplaySymbol(currencySymbol, currencyName, currencyId);
  return displaySymbol ? `${formatted} ${displaySymbol}` : formatted;
}

// Date formatting utilities
// This function formats dates for display based on user's calendar preference
// Input: Date object or ISO string (UTC from database)
// Output: Formatted string in user's preferred calendar (Hijri/Gregorian)
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '';

  const settings = useSettingsStore.getState();
  const { type, format } = settings.dateFormat;

  if (typeof date === 'string') {
    const dateOnly = extractDateOnlyString(date);
    if (dateOnly) {
      return formatDateString(dateOnly, type, format);
    }
  }

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const utcString = dateToUTCString(d);
  if (!utcString) return '';

  return formatDateString(utcString, type, format);
}

/** عرض نطاق تاريخي (مثل السنة المالية) بترتيب صحيح */
export function formatDateRange(
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  options?: { isRTL?: boolean }
): string {
  const from = formatDate(startDate);
  const to = formatDate(endDate);
  if (!from && !to) return '';
  if (!from) return to;
  if (!to) return from;
  return options?.isRTL ? `من ${from} إلى ${to}` : `${from} – ${to}`;
}

// Parse a date string from display format to Date object (UTC)
// Input: Date string in user's preferred format (Hijri/Gregorian)
// Output: Date object in UTC (for database storage)
export function parseDate(dateString: string): Date | null {
  if (!dateString) return null;

  const settings = useSettingsStore.getState();
  const { type, format } = settings.dateFormat;

  // Parse the date string to UTC Gregorian string
  const utcString = parseDateString(dateString, type, format);
  if (!utcString) return null;

  // Convert to Date object
  return utcStringToDate(utcString);
}

// Format date and time
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const dateStr = formatDate(d);
  const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return `${dateStr} ${timeStr}`;
}

/**
 * Convert number to Arabic words (تفقيط)
 * @param num - Number to convert
 * @param currencyName - Currency name (default: 'ريال')
 * @param fractionName - Fraction name (default: 'هللة')
 * @returns Arabic text representation of the number
 */
export function numberToArabicWords(
  num: number | string | null | undefined,
  currencyName: string = 'ريال',
  fractionName: string = 'هللة'
): string {
  if (num === null || num === undefined || num === '') {
    return '';
  }

  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(numValue) || numValue === 0) {
    return 'صفر ' + currencyName;
  }

  const numStr = numValue.toFixed(2);
  const parts = numStr.split('.');
  const integerPart = parseInt(parts[0]);
  const decimalPart = parseInt(parts[1] || '0');

  const ones = [
    '',
    'واحد',
    'اثنان',
    'ثلاثة',
    'أربعة',
    'خمسة',
    'ستة',
    'سبعة',
    'ثمانية',
    'تسعة',
    'عشرة',
    'أحد عشر',
    'اثنا عشر',
    'ثلاثة عشر',
    'أربعة عشر',
    'خمسة عشر',
    'ستة عشر',
    'سبعة عشر',
    'ثمانية عشر',
    'تسعة عشر',
  ];

  const tens = [
    '',
    '',
    'عشرون',
    'ثلاثون',
    'أربعون',
    'خمسون',
    'ستون',
    'سبعون',
    'ثمانون',
    'تسعون',
  ];

  const hundreds = [
    '',
    'مئة',
    'مئتان',
    'ثلاثمئة',
    'أربعمئة',
    'خمسمئة',
    'ستمئة',
    'سبعمئة',
    'ثمانمئة',
    'تسعمئة',
  ];

  const convertGroup = (group: number): string => {
    if (group === 0) return '';

    let result = '';

    // Hundreds
    const h = Math.floor(group / 100);
    if (h > 0) {
      if (h === 1 && group % 100 === 0) {
        return 'مئة';
      }
      result += hundreds[h];
      if (group % 100 > 0) {
        result += ' و';
      }
    }

    // Tens and ones
    const remainder = group % 100;
    if (remainder > 0) {
      if (remainder < 20) {
        result += ones[remainder];
      } else {
        const t = Math.floor(remainder / 10);
        const o = remainder % 10;
        if (o > 0) {
          result += ones[o] + ' و' + tens[t];
        } else {
          result += tens[t];
        }
      }
    }

    return result;
  };

  const convertToWords = (n: number): string => {
    if (n === 0) return 'صفر';

    let words = '';

    // Billions (not used in typical currency amounts, but included for completeness)
    if (n >= 1000000000) {
      const billions = Math.floor(n / 1000000000);
      words += convertGroup(billions);
      if (billions === 1) {
        words += ' مليار';
      } else if (billions === 2) {
        words += ' ملياران';
      } else if (billions > 2 && billions < 11) {
        words += ' مليارات';
      } else {
        words += ' مليار';
      }
      if (n % 1000000000 > 0) {
        words += ' و';
      }
      n = n % 1000000000;
    }

    // Millions
    if (n >= 1000000) {
      const millions = Math.floor(n / 1000000);
      words += convertGroup(millions);
      if (millions === 1) {
        words += ' مليون';
      } else if (millions === 2) {
        words += ' مليونان';
      } else if (millions > 2 && millions < 11) {
        words += ' ملايين';
      } else {
        words += ' مليون';
      }
      if (n % 1000000 > 0) {
        words += ' و';
      }
      n = n % 1000000;
    }

    // Thousands
    if (n >= 1000) {
      const thousandsGroup = Math.floor(n / 1000);
      if (thousandsGroup === 1) {
        words += 'ألف';
      } else if (thousandsGroup === 2) {
        words += 'ألفان';
      } else {
        words += convertGroup(thousandsGroup);
        if (thousandsGroup > 2 && thousandsGroup < 11) {
          words += ' آلاف';
        } else {
          words += ' ألف';
        }
      }
      if (n % 1000 > 0) {
        words += ' و';
      }
      n = n % 1000;
    }

    // Hundreds, tens, and ones
    if (n > 0) {
      words += convertGroup(n);
    }

    return words.trim();
  };

  let result = convertToWords(integerPart);
  
  // Add currency name
  if (integerPart === 1) {
    result += ' ' + currencyName;
  } else if (integerPart === 2) {
    result += ' ' + currencyName + 'ان';
  } else if (integerPart > 2 && integerPart < 11) {
    result += ' ' + currencyName + 'ات';
  } else {
    result += ' ' + currencyName;
  }

  // Add decimal part if exists
  if (decimalPart > 0) {
    result += ' و';
    const decimalWords = convertToWords(decimalPart);
    if (decimalPart === 1) {
      result += decimalWords + ' ' + fractionName;
    } else if (decimalPart === 2) {
      result += decimalWords + ' ' + fractionName + 'تان';
    } else if (decimalPart > 2 && decimalPart < 11) {
      result += decimalWords + ' ' + fractionName + 'ات';
    } else {
      result += decimalWords + ' ' + fractionName;
    }
  }

  // Add "only" suffix
  result += ' فقط';

  return result;
}




